import { defineStore } from 'pinia';
import { api } from '@/services/http.service';

import {
  AvailableAssociablePaymentMethodItem,
  GeoMultiMethodItem,
  AssociatedPaymentMethodItem,
  GeoMultiMethodUpdateRequestPayload,
  GeoMultiMethodCreateRequestPayload,
  GEO_MULTI_METHOD_DEFAULT_STATE,
} from '@/models/ps-management/multi-methods.model';
import { cloneDeep } from 'lodash';
import { ElMessageBox, ElNotification, FormInstance } from 'element-plus';
import { GeoMultiPaymentMethodListItem } from '@/models/ps-management/ps-management.model';
import { usePsManagementCountryListStore } from '@/store/ps-management/payment-methods-country-list.store';
import { useGeoPsManagementStore } from './geo-payment-methods.store';

interface GeoMultiMethodsStore {
  geoMultiMethod: GeoMultiMethodItem;
  isGeoMultiMethodLoading: boolean;
  associatedPaymentMethodList: AssociatedPaymentMethodItem[];
  isAssociatedPaymentMethodListLoading: boolean;
  availableAssociablePaymentMethodList: AvailableAssociablePaymentMethodItem[];
  isAvailableAssociablePaymentMethodListLoading: boolean;
  geoMultiMethodMainSettingsFormRef: null | FormInstance;
  isMethodListAssociatingDialogVisible: boolean;
  isUnsavedChanges: boolean;
}

export const useGeoMultiMethodsStore = defineStore({
  id: 'geoMultiMethodStore',
  state: (): GeoMultiMethodsStore => ({
    geoMultiMethod: cloneDeep(GEO_MULTI_METHOD_DEFAULT_STATE),
    isGeoMultiMethodLoading: false,
    associatedPaymentMethodList: [],
    isAssociatedPaymentMethodListLoading: false,
    availableAssociablePaymentMethodList: [],
    isAvailableAssociablePaymentMethodListLoading: false,
    geoMultiMethodMainSettingsFormRef: null,
    isMethodListAssociatingDialogVisible: false,
    isUnsavedChanges: false,
  }),
  actions: {
    setDefaultGeoMultiMethodStoreState(): void {
      this.geoMultiMethod = cloneDeep(GEO_MULTI_METHOD_DEFAULT_STATE);
      this.associatedPaymentMethodList = [];
      this.isUnsavedChanges = false;
    },
    setFormChanged(isChanged: boolean): void {
      this.isUnsavedChanges = isChanged;
    },
    openMethodsAssociatingDialog(): void {
      if (!this.geoMultiMethod.multiMethodId) {
        ElNotification({
          title: 'Error',
          message: 'Please save the multi-method before associating payment methods',
          type: 'error',
        });

        return;
      }

      this.isMethodListAssociatingDialogVisible = true;
      this.getAvailableAssociablePaymentMethodList(this.geoMultiMethod.multiMethodId);
    },
    closeMethodsAssociatingDialog(): void {
      this.isMethodListAssociatingDialogVisible = false;
    },
    associateMethodsToMultiMethod(selectedMethods: AvailableAssociablePaymentMethodItem[]): Promise<void> {
      const { multiMethodId } = this.geoMultiMethod;

      if (!multiMethodId) {
        ElNotification({
          title: 'Error',
          message: 'Can\'t associate methods: no multi-method id',
          type: 'error',
        });

        return Promise.resolve();
      }

      const billingPaymentMethodIdList = selectedMethods.map(({ billingPaymentMethodId }) => billingPaymentMethodId);

      this.isGeoMultiMethodLoading = true;

      return this.associateBillingPaymentMethod({
        multiMethodId,
        payloadData: {
          billingPaymentMethodIdList,
        },
      })
        .then(() => {
          this.closeMethodsAssociatingDialog();

          ElNotification({
            title: 'Success',
            message: 'Methods have been associated',
            type: 'success',
          });
        })
        .finally(() => {
          this.isGeoMultiMethodLoading = false;
        });
    },
    getGeoMultiMethod(multiMethodId: string): Promise<GeoMultiMethodItem | void> {
      this.isGeoMultiMethodLoading = true;

      return api.psManagement.getGeoMultiMethodRequest.perform(multiMethodId)
        .then((response: GeoMultiMethodItem) => {
          this.geoMultiMethod = response;
          this.setFormChanged(false);

          return response;
        })
        .finally(() => {
          this.isGeoMultiMethodLoading = false;
        });
    },
    createGeoMultiMethod(createdMethodPayload: GeoMultiMethodItem): Promise<string | void> {
      this.isGeoMultiMethodLoading = true;

      const { selectedCountry } = usePsManagementCountryListStore();

      if (!selectedCountry) {
        ElNotification({
          title: 'Error',
          message: 'Can\'t create method: no country selected',
          type: 'error',
        });

        return Promise.resolve();
      }

      const requestPayload: GeoMultiMethodCreateRequestPayload = {
        publicName: createdMethodPayload.publicName,
        imageId: createdMethodPayload.imageId,
        withdrawalAvailable: createdMethodPayload.withdrawalAvailable,
        currency: createdMethodPayload.currency,
        country: selectedCountry.code,
        organic: createdMethodPayload.organic,
        segmentList: createdMethodPayload.segmentList,
        excludedSegmentList: createdMethodPayload.excludedSegmentList,
      };

      return api.psManagement.createGeoMultiMethodRequest.perform(requestPayload)
        .then((multiMethodId) => {
          return multiMethodId;
        })
        .finally(() => {
          this.isGeoMultiMethodLoading = false;
        });
    },
    updateGeoMultiMethod(payload: GeoMultiMethodItem): Promise<GeoMultiMethodItem | void> {
      this.isGeoMultiMethodLoading = true;

      return api.psManagement.updateGeoMultiMethodRequest.perform(payload as GeoMultiMethodUpdateRequestPayload)
        .then(() => {
          ElNotification({
            title: 'Success',
            message: 'Multi method has been updated',
            type: 'success',
          });
          this.setFormChanged(false);
        })
        .catch((error) => {
          this.setFormChanged(true);

          throw error;
        })
        .finally(() => {
          this.isGeoMultiMethodLoading = false;
        });
    },
    updateGeoMultiPaymentMethodEnabledState(): void {
      ElMessageBox.confirm(
        `Are you sure you want to ${this.geoMultiMethod.enabled ? 'disable' : 'enable'} this multi method?`,
        {
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
          type: 'warning',
          lockScroll: false,
        },
      )
        .then(() => {
          this.updateGeoMultiMethod({
            ...this.geoMultiMethod,
            enabled: !this.geoMultiMethod.enabled,
          })
            .then(() => {
              this.geoMultiMethod.enabled = !this.geoMultiMethod.enabled;
            })
            .finally(() => {
              this.setFormChanged(false);
            });
        });
    },
    updateGeoMultiMethodWithdrawState(
      multiMethodListItem: GeoMultiPaymentMethodListItem,
    ): Promise<GeoMultiMethodItem | void> {
      const { selectedCountry } = usePsManagementCountryListStore();
      const { getGeoPaymentMethodList } = useGeoPsManagementStore();

      if (!selectedCountry) {
        ElNotification({
          title: 'Error',
          message: 'No country selected',
          type: 'error',
        });

        return Promise.resolve();
      }

      return this.updateGeoMultiMethod({
        ...multiMethodListItem,
        country: selectedCountry.code,
      })
        .finally(() => {
          getGeoPaymentMethodList();
        });
    },
    deleteGeoMultiMethod(multiMethodId: string): Promise<{} | void> {
      const { setGeoPaymentMethodListLoading } = useGeoPsManagementStore();
      setGeoPaymentMethodListLoading(true);
      this.isGeoMultiMethodLoading = true;

      return api.psManagement.deleteGeoMultiMethodRequest.perform(multiMethodId)
        .finally(() => {
          this.isGeoMultiMethodLoading = false;
          setGeoPaymentMethodListLoading(false);
        });
    },
    associateBillingPaymentMethod(
      payload: {
        multiMethodId: string;
        payloadData: {
          billingPaymentMethodIdList: string[];
        };
      },
    ): Promise<void> {
      this.isAssociatedPaymentMethodListLoading = true;

      return api.psManagement.associateBillingMethodListRequest.perform(payload)
        .then(() => {
          this.getAssociatedPaymentMethodList(payload.multiMethodId);
        })
        .finally(() => {
          this.isAssociatedPaymentMethodListLoading = false;
        });
    },
    disassociateBillingPaymentMethod(
      payload: {
        multiMethodId: string;
        billingPaymentMethodId: string;
      },
    ): Promise<void> {
      this.isAssociatedPaymentMethodListLoading = true;

      return api.psManagement.disassociateBillingMethodRequest.perform(payload)
        .then(() => {
          this.getAssociatedPaymentMethodList(payload.multiMethodId);
        })
        .finally(() => {
          this.isAssociatedPaymentMethodListLoading = false;
        });
    },
    updateAssociatedBillingPaymentMethod(payload: {
      multiMethodId: string;
      associatedBillingMethodData: AssociatedPaymentMethodItem;
    }): Promise<void> {
      this.isAssociatedPaymentMethodListLoading = true;

      return api.psManagement.updateAssociatedPaymentMethodRequest.perform(payload)
        .then(() => {
          this.getAssociatedPaymentMethodList(payload.multiMethodId);
        })
        .catch((error) => {
          throw error;
        })
        .finally(() => {
          this.isAssociatedPaymentMethodListLoading = false;
        });
    },
    getAssociatedPaymentMethodList(multiMethodId: string): Promise<AssociatedPaymentMethodItem[]> {
      this.isAssociatedPaymentMethodListLoading = true;

      return api.psManagement.getAssociatedPaymentMethodListRequest.perform(multiMethodId)
        .then((response: AssociatedPaymentMethodItem[]) => {
          this.associatedPaymentMethodList = response;

          return response;
        })
        .finally(() => {
          this.isAssociatedPaymentMethodListLoading = false;
        });
    },
    getAvailableAssociablePaymentMethodList(multiMethodId: string): Promise<AvailableAssociablePaymentMethodItem[]> {
      this.isAvailableAssociablePaymentMethodListLoading = true;

      return api.psManagement.getAvailableAssociablePaymentMethodIListRequest.perform(multiMethodId)
        .then((response: AvailableAssociablePaymentMethodItem[]) => {
          this.availableAssociablePaymentMethodList = response;

          return response;
        })
        .finally(() => {
          this.isAvailableAssociablePaymentMethodListLoading = false;
        });
    },
  },
});
